import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  FormControlLabelProps,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMediaDevices } from "react-media-devices";
import { Link } from "react-router-dom";
import useSWR from "swr";

const localStorageKey = "resale-photo-booth/device-map";

function getDeviceMapping(): (MediaDeviceInfo | null)[] {
  const mapping = localStorage.getItem(localStorageKey);
  return mapping ? JSON.parse(mapping) : [null, null, null, null, null];
}

export function useSavedCameraConfiguration() {
  const { data, mutate } = useSWR<(MediaDeviceInfo | null)[]>("photobooth-saved-camera-configuration", async () => {
    return getDeviceMapping();
  });

  const persistingMutate = (devices: (MediaDeviceInfo | null)[]) => {
    localStorage.setItem(localStorageKey, JSON.stringify(devices));
    mutate(devices);
  };

  return { savedCameraConfiguration: data ?? getDeviceMapping(), mutate: persistingMutate };
}

const SelectDevicePosition = ({
  index,
  FormLabelProps,
  devices,
}: {
  index: number;
  FormLabelProps: Partial<FormControlLabelProps>;
  devices: MediaDeviceInfo[];
}) => {
  const { savedCameraConfiguration, mutate } = useSavedCameraConfiguration();

  return (
    <FormControlLabel
      {...FormLabelProps}
      control={
        <TextField
          select
          size="small"
          sx={{ width: 250 }}
          defaultValue=""
          onChange={(event) => {
            if (!devices) return;
            const device = devices.find(({ deviceId }) => deviceId === event.target.value);
            if (!device) return;
            const newConfig = [...savedCameraConfiguration];
            newConfig[index] = device;
            mutate(newConfig);
          }}
          value={savedCameraConfiguration[index]?.deviceId ?? ""}
        >
          {devices &&
            devices.map((device) => (
              <MenuItem key={device.deviceId} value={device.deviceId}>
                {device.deviceId.substring(0, 4).toUpperCase()}: {device.label}
              </MenuItem>
            ))}
        </TextField>
      }
      label={<Typography variant="h6">{FormLabelProps.label}</Typography>}
      labelPlacement="top"
    />
  );
};

export function ConfigurationPage() {
  const { devices, loading } = useMediaDevices({ constraints: { video: true } });

  return (
    <Box component={Paper} variant="outlined" padding={4}>
      <Stack spacing={4}>
        <Stack>
          <Typography fontSize="24px" fontWeight={600}>
            Camera Setup
          </Typography>
          <Typography fontSize="14px" fontWeight={400}>
            Configure the position of each camera. The configuration will be saved locally.
          </Typography>
        </Stack>
        {loading && <Typography>Loading media devices...</Typography>}
        {devices && (
          <Stack direction="row" spacing={2}>
            <SelectDevicePosition index={0} FormLabelProps={{ label: "Front Camera" }} devices={devices} />
            <SelectDevicePosition index={1} FormLabelProps={{ label: "Left Camera" }} devices={devices} />
            <SelectDevicePosition index={2} FormLabelProps={{ label: "Right Camera" }} devices={devices} />
            <SelectDevicePosition index={3} FormLabelProps={{ label: "Top Camera" }} devices={devices} />
            <SelectDevicePosition index={4} FormLabelProps={{ label: "Hero Camera" }} devices={devices} />
          </Stack>
        )}
        <Divider />
        <Stack direction="row" spacing={2} justifyContent="end">
          <Button component={Link} to={"/resale-photo-booth"} sx={{ paddingX: 3 }} variant="contained">
            Save Configuration
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
